import { useTheme } from "@mui/material";
import type { ReactNode } from "react";
import { usePropertyQuery } from "web_myplann/Api/Portfolio/Queries/usePropertyQuery";
import type {
	ClientPropertyTypeEnum,
	GetPropertyIdV2FetchResponse,
} from "web_myplann/Infrastructure/Api/Api";
import { DetailSectionLoader } from "web_myplann/Portfolio/Components/DetailSectionLoader";
import { getPropertyDetailRoute } from "web_myplann/Portfolio/Helpers/getPropertyDetailRoute";
import { useEditAccessGuard } from "web_myplann/Portfolio/Hooks/useEditAccessGuard";
import { useItemNotFoundRedirect } from "web_myplann/Portfolio/Hooks/useItemNotFoundRedirect";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import NavigationHeader from "web_myplann/Shared/Header/NavigationHeader";
import { useParsedParam } from "web_myplann/Shared/Hooks/useParsedParam";
import { parseToFiniteNumber } from "web_myplann/Shared/Utils/parsers";

type PropertyDetail = Extract<
	GetPropertyIdV2FetchResponse,
	{ status: 200 }
>["data"];

type Props<TProperty extends PropertyDetail> = {
	expectedTypes: ClientPropertyTypeEnum[];
	title: string;
	children: (ctx: {
		property: TProperty;
		propertyId: number;
		detailRoute: string;
	}) => ReactNode;
};

export const PropertyEditLayout = <TProperty extends PropertyDetail>({
	expectedTypes,
	title,
	children,
}: Props<TProperty>) => {
	const theme = useTheme();
	const propertyId = useParsedParam("id", parseToFiniteNumber);

	const { data: property, error, isLoading } = usePropertyQuery(propertyId);
	useItemNotFoundRedirect(error);

	const detailRoute = getPropertyDetailRoute(propertyId);

	const { isLockedByAdvisor } = useEditAccessGuard(property, detailRoute);

	const isExpectedType =
		property?.type !== undefined && expectedTypes.includes(property.type);
	const canRenderChildren =
		property &&
		isExpectedType &&
		!isLockedByAdvisor &&
		propertyId !== undefined;

	return (
		<MyPlannLayout
			bgColor={theme.palette.grey[50]}
			disableContentPadding
			topBarBgColor={theme.palette.background.paper}
			topBar={
				<NavigationHeader
					title={title}
					bgColor={theme.palette.background.paper}
					defaultRoute={detailRoute}
					replaceOnBack
					backIconColor={theme.palette.grey[900]}
					backIconSize={theme.iconSize.md}
				/>
			}
		>
			{isLoading ? (
				<DetailSectionLoader />
			) : canRenderChildren ? (
				children({
					property: property as TProperty,
					propertyId,
					detailRoute,
				})
			) : null}
		</MyPlannLayout>
	);
};
